import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopAroundListBodyBlockConfig } from '@front-platform/content-builder';
import { shopAroundListBodyBlockResolveConfig } from '@front-platform/content-builder';
import { Component, inject, OnInit, input } from '@angular/core';
import { Router } from '@angular/router';

import { NativeService } from '@core/services/native/native.service';
import { EntitiesService } from '@core/services/entities/entities.service';
import { RouterHelperService } from 'src/app/core/services/router-helper/router-helper.service';

import { Paths } from '@config/paths.config';
import { EntityKeys } from '@config/keys/entity.keys';

import { DoodEntityModel } from '@core/models/entity.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import { TemplateLayoutKeys } from '@config/keys/template-layout.keys';

import { SHOP_AROUND_LIST_BODY_BLOCK_DATA_SOURCE } from './shop-around-list-body-block-view-model';
import { provideShopAroundListBodyBlockDataSource } from './shop-around-list-body-block-data-source.providers';
export interface ShopAroundListBodyBlockRuntimeConfig {}

export type ShopAroundListBodyBlockConfigInput = Partial<
  Omit<ShopAroundListBodyBlockConfig, keyof ShopAroundListBodyBlockRuntimeConfig> &
    ShopAroundListBodyBlockRuntimeConfig
>;
export type ShopAroundListBodyBlockResolvedConfig = Omit<
  ShopAroundListBodyBlockConfig,
  keyof ShopAroundListBodyBlockRuntimeConfig
> &
  Partial<ShopAroundListBodyBlockRuntimeConfig>;
export interface ShopAroundListBodyBlockContract extends ConfigComponentContract<
  ShopAroundListBodyBlockConfigInput,
  ShopAroundListBodyBlockResolvedConfig
> {}

@Component({
  selector: 'app-shop-around-list-body-block',
  templateUrl: './shop-around-list-body-block.component.html',
  standalone: false,
  providers: [...provideShopAroundListBodyBlockDataSource()],
})
export class ShopAroundListBodyBlockComponent implements OnInit, ShopAroundListBodyBlockContract {
  readonly config = input<ShopAroundListBodyBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopAroundListBodyBlockConfig,
    ShopAroundListBodyBlockRuntimeConfig
  >(shopAroundListBodyBlockResolveConfig, () => ({ config: this.config() }));
  get heading(): string | undefined {
    return this.resolvedConfig()['heading'] as unknown as string | undefined;
  }
  get headingColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['heading_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get displayEntityImages(): boolean {
    return this.resolvedConfig()['display_entity_images'] as unknown as boolean;
  }
  get displayEntityDistances(): boolean {
    return this.resolvedConfig()['display_entity_distances'] as unknown as boolean;
  }
  get displayEntityPricings(): boolean {
    return this.resolvedConfig()['display_entity_pricings'] as unknown as boolean;
  }
  get displayEntityAddresses(): boolean {
    return this.resolvedConfig()['display_entity_addresses'] as unknown as boolean;
  }
  get displayEntityCategories(): boolean {
    return this.resolvedConfig()['display_entity_categories'] as unknown as boolean;
  }
  get isShopNameVisible(): boolean {
    return this.resolvedConfig()['is_shop_name_visible'] as unknown as boolean;
  }
  get entityNamesSize(): string {
    return this.resolvedConfig()['entity_names_size'] as unknown as string;
  }
  get displaySeeShopMapButton(): boolean {
    return this.resolvedConfig()['display_see_shop_map_button'] as unknown as boolean;
  }
  get shopsMapButtonColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shops_map_button_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopsMapButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shops_map_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get templateLayout(): TemplateLayoutKeys {
    return this.resolvedConfig()['template_layout'] as unknown as TemplateLayoutKeys;
  }
  get entityNameColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['entity_name_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get entityMetaColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['entity_meta_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get entityAddressColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['entity_address_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  entityKeys = EntityKeys;
  templateLayoutKeys = TemplateLayoutKeys;
  isIos = false;

  private readonly blockDataSource = inject(SHOP_AROUND_LIST_BODY_BLOCK_DATA_SOURCE);

  protected readonly vm$ = this.blockDataSource.vm$;

  constructor(
    private readonly router: Router,
    private readonly entitiesService: EntitiesService,
    private readonly routerHelper: RouterHelperService,
  ) {}

  ngOnInit(): void {
    this.isIos = NativeService.platform === 'ios';
  }

  entityUrl(entity: DoodEntityModel): string {
    return this.entitiesService.getUrl(entity);
  }

  entityAddress(entity: DoodEntityModel): string {
    return this.entitiesService.getFullAddress(entity);
  }

  navigateToMapPage(): void {
    this.router.navigate([this.routerHelper.translateRoute(`${Paths.ShopsMap}`)]);
  }

  shopImageCoverUrl(shop: DoodEntityModel): string {
    const _images = shop[EntityKeys.Images] ?? {};

    if (_images.overview?.url) {
      return _images.overview.url;
    }

    if (_images.cover?.url) {
      return _images.cover.url;
    }

    if (shop.cover) {
      return 'https://api.dood.com' + shop.cover;
    }

    return '';
  }

  protected getShopCurrency(shop: DoodEntityModel): string {
    // satisfy TS for strictTemplate (not the best solution)
    return (shop as unknown as { [EntityKeys.Currency]: string })[this.entityKeys.Currency] ?? '';
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopAroundListBodyBlockComponent, {
  'keyAliases': {},
});
